import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, GuidedMethodology } from "../../lib/firebase";
import { GuidedMethodologyEditDialog } from "./GuidedMethodologyEditDialog";
import { toast } from "../../components/ui/use-toast";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminGuidedMethodologies = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [methodologies, setMethodologies] = useState<GuidedMethodology[]>([]);
  const [error, setError] = useState<string | null>(null);
  const auth = getAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
    });

    // Load guided methodologies
    const loadGuidedMethodologies = async () => {
      try {
        setLoading(true);
        const guidedMethodologies = await methodologyService.getGuidedMethodologies();
        setMethodologies(guidedMethodologies);
        setLoading(false);
      } catch (error) {
        console.error("Error loading guided methodologies:", error);
        setError("Failed to load guided methodologies. Please try again.");
        setLoading(false);
      }
    };

    loadGuidedMethodologies();

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCreateMethodology = () => {
    setIsCreateDialogOpen(true);
  };

  const handleSaveNewMethodology = async (data: { name: string; description: string; status: 'Active' | 'Inactive' | 'Draft'; keywords?: string[] }) => {
    try {
      const newMethodology: Omit<GuidedMethodology, 'id'> = {
        name: data.name,
        description: data.description,
        status: data.status,
        keywords: data.keywords || [],
        steps: []
      };
      
      const newId = await methodologyService.createGuidedMethodology(newMethodology);
      
      if (newId) {
        toast({
          title: "Methodology Created",
          description: "New guided methodology has been created successfully.",
        });
        
        // Refresh the list
        const updatedMethodologies = await methodologyService.getGuidedMethodologies();
        setMethodologies(updatedMethodologies);
        
        // Close the dialog
        setIsCreateDialogOpen(false);
        
        // Navigate to the edit page for the new methodology
        navigate(`/aAyuiOcvMq/guided-methodologies/${newId}`);
      } else {
        setError("Failed to create new methodology. Please try again.");
      }
    } catch (error) {
      console.error("Error creating methodology:", error);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleEditMethodology = (id: string) => {
    navigate(`/aAyuiOcvMq/guided-methodologies/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <GuidedMethodologyEditDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSave={handleSaveNewMethodology}
        name=""
        description=""
        status="Draft"
        keywords={[]}
        isNew={true}
      />

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                Guided Methodologies
              </h1>
              <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                Manage guided assessment methodologies
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                className="h-10 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => navigate('/aAyuiOcvMq/dashboard')}
              >
                Back to Dashboard
              </Button>
              <Button 
                className="h-10 px-4 bg-[#daed6e] text-black rounded-lg hover:bg-[#c5d85c] transition-colors"
                onClick={handleCreateMethodology}
              >
                Create New
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>{error}</p>
            </div>
          )}

          {/* Methodologies List */}
          <div className="space-y-4">
            {methodologies.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No guided methodologies found.</p>
                <p className="text-gray-500 text-sm mt-2">Click "Create New" to add a methodology.</p>
              </div>
            ) : (
              methodologies.map((methodology) => (
                <div 
                  key={methodology.id} 
                  className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black">
                        {methodology.name}
                      </h3>
                      <p className="text-sm text-black opacity-80 mt-1 line-clamp-2">
                        {methodology.description || "No description provided"}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          methodology.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : methodology.status === 'Inactive'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {methodology.status}
                        </span>
                        {methodology.keywords && methodology.keywords.length > 0 && (
                          <span className="text-xs text-gray-500">
                            {methodology.keywords.length} keywords
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      className="h-9 px-4 bg-[#daed6e] text-black rounded-lg hover:bg-[#c5d85c] transition-colors"
                      onClick={() => handleEditMethodology(methodology.id)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 